import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { apiFetch, apiFetchEmpty, authorizedFetch } from './client';
import { keys } from './keys';
import { invalidateAllCommunityAudits } from './communityAudit';
import { PhasesListSchema } from './schemas';

export function usePhasesTree() {
  return useQuery({
    queryKey: keys.phases(),
    queryFn: () => apiFetch('/api/phases', PhasesListSchema),
  });
}

export function useItemsMaster() {
  return useQuery({
    queryKey: ['items', 'master'],
    staleTime: 60_000,
    queryFn: () => apiFetch('/api/items', z.array(z.record(z.string(), z.unknown()))),
  });
}

export function useCostTypes() {
  return useQuery({
    queryKey: ['cost-types'],
    queryFn: () => apiFetch('/api/cost-types', z.array(z.record(z.string(), z.unknown()))),
  });
}

export function useCreateCostType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch('/api/cost-types', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cost-types'] }),
  });
}

export function useUpdateCostType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: { name?: string } }) =>
      apiFetchEmpty(`/api/cost-types/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cost-types'] });
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      invalidateAllCommunityAudits(qc);
    },
  });
}

export function useReorderCostTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      apiFetchEmpty('/api/cost-types/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cost-types'] }),
  });
}

export function useDeleteCostType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/cost-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['cost-types'] }),
  });
}

const masterTableUsageEntrySchema = z.object({
  id: z.coerce.number(),
  label: z.string(),
});

export const masterTableUsageSchema = z.object({
  items: z.array(masterTableUsageEntrySchema).default([]),
  models: z.array(masterTableUsageEntrySchema).default([]),
  payableTaskGroups: z.array(masterTableUsageEntrySchema).default([]),
  communities: z.array(masterTableUsageEntrySchema).default([]),
});

export type MasterTableUsage = z.infer<typeof masterTableUsageSchema>;

export function isMasterTableUsageBlocked(usage?: MasterTableUsage): boolean {
  if (!usage) return false;
  return (
    usage.items.length > 0 ||
    usage.models.length > 0 ||
    usage.payableTaskGroups.length > 0 ||
    usage.communities.length > 0
  );
}

export function useCostTypeUsage(costTypeId: number | null) {
  return useQuery({
    queryKey: ['cost-types', costTypeId, 'usage'],
    queryFn: () => apiFetch(`/api/cost-types/${costTypeId}/usage`, masterTableUsageSchema),
    enabled: costTypeId != null,
  });
}

export function useProductTypes() {
  return useQuery({
    queryKey: ['product-types'],
    queryFn: () => apiFetch('/api/product-types', z.array(z.record(z.string(), z.unknown()))),
  });
}

export function useCreateProductType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch('/api/product-types', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['product-types'] }),
  });
}

export function useUpdateProductType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: { name?: string } }) =>
      apiFetchEmpty(`/api/product-types/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['product-types'] }),
  });
}

export function useReorderProductTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      apiFetchEmpty('/api/product-types/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['product-types'] }),
  });
}

export function useDeleteProductType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/product-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['product-types'] }),
  });
}

const productTypeModelUsageSchema = masterTableUsageSchema;

export function useProductTypeModelUsage(productTypeId: number | null) {
  return useQuery({
    queryKey: ['product-types', productTypeId, 'models'],
    queryFn: () => apiFetch(`/api/product-types/${productTypeId}/models`, productTypeModelUsageSchema),
    enabled: productTypeId != null,
  });
}

export function useFloorTypeModelUsage(floorTypeId: number | null) {
  return useQuery({
    queryKey: ['floor-types', floorTypeId, 'models'],
    queryFn: () => apiFetch(`/api/floor-types/${floorTypeId}/models`, masterTableUsageSchema),
    enabled: floorTypeId != null,
  });
}

export function useFloorTypes() {
  return useQuery({
    queryKey: ['floor-types'],
    queryFn: () => apiFetch('/api/floor-types', z.array(z.record(z.string(), z.unknown()))),
  });
}

export function useCreateFloorType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch('/api/floor-types', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['floor-types'] }),
  });
}

export function useUpdateFloorType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: { name?: string } }) =>
      apiFetchEmpty(`/api/floor-types/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['floor-types'] }),
  });
}

export function useReorderFloorTypes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      apiFetchEmpty('/api/floor-types/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['floor-types'] }),
  });
}

export function useDeleteFloorType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/floor-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['floor-types'] }),
  });
}

export function useScopeCommunityUsage(scopeId: number | null) {
  return useQuery({
    queryKey: ['scopes', scopeId, 'communities'],
    queryFn: () => apiFetch(`/api/scopes/${scopeId}/communities`, masterTableUsageSchema),
    enabled: scopeId != null,
  });
}

export function useScopes() {
  return useQuery({
    queryKey: ['scopes'],
    queryFn: () => apiFetch('/api/scopes', z.array(z.record(z.string(), z.unknown()))),
  });
}

export function useCreateScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string }) =>
      apiFetch('/api/scopes', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scopes'] }),
  });
}

export function useUpdateScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: { name?: string } }) =>
      apiFetchEmpty(`/api/scopes/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scopes'] }),
  });
}

export function useReorderScopes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      apiFetchEmpty('/api/scopes/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scopes'] }),
  });
}

export function useDeleteScope() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/scopes/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['scopes'] }),
  });
}

export type ItemsXlsxImportResult = {
  success?: boolean;
  added?: number;
  updated?: number;
  errors?: string[];
  error?: string;
};

export function useImportItemsXlsx() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authorizedFetch('/api/import/items-xlsx', { method: 'POST', body: fd });
      const data = (await res.json().catch(() => ({}))) as ItemsXlsxImportResult;
      if (!res.ok) throw new Error(data.error || 'Import failed');
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      void qc.invalidateQueries({ queryKey: keys.phases() });
      invalidateAllCommunityAudits(qc);
    },
  });
}

export function useImportPhasesXlsx() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await authorizedFetch('/api/import/phases-xlsx', { method: 'POST', body: fd });
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Import failed');
      return data;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useBulkDeleteItems() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      apiFetchEmpty('/api/items/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      invalidateAllCommunityAudits(qc);
    },
  });
}

export type ItemPhaseInput = {
  phase_id: number | string | null | undefined;
  sub_phase_id?: number | string | null;
};

export type ItemPayload = {
  name: string;
  item_code?: string | null;
  unit?: string | null;
  cost_type_id?: number | null;
  phases: ItemPhaseInput[];
  is_active?: boolean;
  aliases?: string[];
};

export function useCreateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ItemPayload) =>
      apiFetch('/api/items', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      void qc.invalidateQueries({ queryKey: ['customers', 'models'] });
      invalidateAllCommunityAudits(qc);
    },
  });
}

export function useUpdateItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; payload: Partial<ItemPayload> }) =>
      apiFetchEmpty(`/api/items/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.payload),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      invalidateAllCommunityAudits(qc);
    },
  });
}

export function useToggleItemActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; is_active: boolean }) =>
      apiFetchEmpty(`/api/items/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: vars.is_active }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['items', 'master'] });
      invalidateAllCommunityAudits(qc);
    },
  });
}

export function useCreatePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; cost_code?: string | null; sort_order?: number }) =>
      apiFetch('/api/phases', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useUpdatePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: { name?: string; cost_code?: string | null; sort_order?: number };
    }) =>
      apiFetchEmpty(`/api/phases/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useReorderPhases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      apiFetchEmpty('/api/phases/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useReorderSubPhases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { phaseId: number; ids: number[] }) =>
      apiFetchEmpty('/api/sub-phases/reorder', {
        method: 'PUT',
        body: JSON.stringify({ phase_id: vars.phaseId, ids: vars.ids }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useDeletePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/phases/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function usePhaseUsage(phaseId: number | null) {
  return useQuery({
    queryKey: ['phases', phaseId, 'usage'],
    queryFn: () => apiFetch(`/api/phases/${phaseId}/usage`, masterTableUsageSchema),
    enabled: phaseId != null,
  });
}

export function useCreateSubPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      phase_id: number;
      name: string;
      cost_code?: string | null;
      sort_order?: number;
    }) =>
      apiFetch('/api/sub-phases', z.object({ id: z.coerce.number() }), {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useUpdateSubPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: number;
      body: { name?: string; cost_code?: string | null; sort_order?: number };
    }) =>
      apiFetchEmpty(`/api/sub-phases/${vars.id}`, {
        method: 'PUT',
        body: JSON.stringify(vars.body),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useDeleteSubPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetchEmpty(`/api/sub-phases/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.phases() }),
  });
}

export function useSubPhaseUsage(subPhaseId: number | null) {
  return useQuery({
    queryKey: ['sub-phases', subPhaseId, 'usage'],
    queryFn: () => apiFetch(`/api/sub-phases/${subPhaseId}/usage`, masterTableUsageSchema),
    enabled: subPhaseId != null,
  });
}
